import { Icon, OspToast, deviceDetector, ospThemes } from "@ospweb/sdk-ui";
import {
  getFileDownloadCapability,
  isLikelyFileDownloadNotSupportedError,
} from "@/src/lib/bridge/downloadCapability";
import { useAppFeatures, useBridge } from "@/src/lib/bridge/useBridge";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { I18nUtil } from "@utils/i18n";
import { ImageLightbox } from "./InlineAttachment";
import { DeletedAttachmentChip } from "./DeletedAttachmentChip";
import Translations from "@translate/components/Chat/FileTagAttachmentList";
import {
  buildAttachmentRequestUrl,
  getAttachmentKey,
  getChatApiBaseUrl,
  getDownloadableFileName,
  getPublicPreviewUrl,
  isImageAttachment,
  resolveMessageAttachmentPreviewUrls,
  revokeBlobPreviewEntries,
  type MessageAttachment,
} from "@utils/chatUtils/chatAttachmentUtils";
import { cn } from "utils";
import { getSenderAttachmentChipVisual } from "../Sender/senderAttachmentChipStyle";
import { rootStore } from "@store/rootStore";
import { useFileDownloadBridge } from "@/src/lib/bridge/useFileDownloadBridge";
import { useRequestHeader } from "@/src/hooks/useRequestHeader";
import { useSnapshot } from "valtio";
import NextImage from "../NextImage";

export type { MessageAttachment };

const I18n = I18nUtil.createScoped(Translations);

interface FileTagAttachmentListProps {
  attachments: MessageAttachment[];
  userId?: string;
  chatId?: string;
  shareId?: string;
  align?: "left" | "right";
  className?: string;
}

const FileTagAttachmentList = ({
  attachments,
  userId,
  chatId,
  shareId,
  align = "left",
  className,
}: FileTagAttachmentListProps) => {
  const [loadingName, setLoadingName] = useState<string>("");
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const previewUrlsRef = useRef<Record<string, string>>({});
  // Images whose preview could not be fetched render as a retryable chip instead of a spinner.
  const [failedKeys, setFailedKeys] = useState<Set<string>>(() => new Set());
  const failedKeysRef = useRef<Set<string>>(new Set());
  const [previewRetry, setPreviewRetry] = useState(0);
  const [lightboxAttachment, setLightboxAttachment] =
    useState<MessageAttachment | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [activeTask, setActiveTask] = useState<{
    taskId: string;
    mode: "open" | "lightbox";
  } | null>(null);
  const { headers, isReady: isRequestHeaderReady } = useRequestHeader();
  const { fingerprint } = useSnapshot(rootStore.user);
  const appFeatures = useAppFeatures();
  const { isInApp } = useBridge();
  const { startDownload, getTask } = useFileDownloadBridge();
  const activeTaskState = getTask(activeTask?.taskId);
  const fileDownloadCapability = getFileDownloadCapability(
    deviceDetector.isApp() || isInApp,
    appFeatures,
  );

  const chatApiBaseUrl = useMemo(() => getChatApiBaseUrl(), []);

  const requestHeaders = useMemo(
    () => ({
      ...headers,
      ...(fingerprint ? { guestId: String(fingerprint) } : {}),
    }),
    [headers, fingerprint],
  );

  useEffect(() => {
    const attachmentKeys = new Set(attachments.map(getAttachmentKey));
    const nextPreviewMap: Record<string, string> = {
      ...previewUrlsRef.current,
    };
    let changed = false;

    for (const key of Object.keys(nextPreviewMap)) {
      if (!attachmentKeys.has(key)) {
        const staleUrl = nextPreviewMap[key];
        if (staleUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(staleUrl);
        }
        delete nextPreviewMap[key];
        changed = true;
      }
    }

    if (changed) {
      previewUrlsRef.current = nextPreviewMap;
      setPreviewUrls(nextPreviewMap);
    }
  }, [attachments]);

  useEffect(() => {
    let cancelled = false;

    const loadImagePreviews = async () => {
      if (!isRequestHeaderReady) {
        return;
      }
      const pendingImageAttachments = attachments
        .filter((attachment) => !attachment.deleted)
        .filter(isImageAttachment)
        .filter((attachment) => {
          const key = getAttachmentKey(attachment);
          return (
            !previewUrlsRef.current[key] && !failedKeysRef.current.has(key)
          );
        });

      if (!pendingImageAttachments.length) {
        return;
      }

      const loadedEntries = await resolveMessageAttachmentPreviewUrls(
        pendingImageAttachments,
        {
          baseUrl: chatApiBaseUrl,
          shareId,
          userId,
          chatId,
          headers: requestHeaders,
        },
      );

      if (!cancelled) {
        const loadedKeys = new Set(loadedEntries.map((entry) => entry.key));
        const newlyFailed = pendingImageAttachments
          .map(getAttachmentKey)
          .filter((key) => !loadedKeys.has(key));
        if (newlyFailed.length) {
          const nextFailed = new Set(failedKeysRef.current);
          newlyFailed.forEach((key) => nextFailed.add(key));
          failedKeysRef.current = nextFailed;
          setFailedKeys(nextFailed);
        }
      }

      if (cancelled || !loadedEntries.length) {
        if (cancelled) {
          revokeBlobPreviewEntries(loadedEntries);
        }
        return;
      }

      const nextPreviewMap = {
        ...previewUrlsRef.current,
      };
      loadedEntries.forEach((entry) => {
        nextPreviewMap[entry.key] = entry.url;
      });
      previewUrlsRef.current = nextPreviewMap;
      setPreviewUrls(nextPreviewMap);
    };

    loadImagePreviews();

    return () => {
      cancelled = true;
    };
  }, [
    attachments,
    chatApiBaseUrl,
    userId,
    chatId,
    shareId,
    requestHeaders,
    isRequestHeaderReady,
    previewRetry,
  ]);

  const retryPreview = useCallback((key: string) => {
    const nextFailed = new Set(failedKeysRef.current);
    nextFailed.delete(key);
    failedKeysRef.current = nextFailed;
    setFailedKeys(nextFailed);
    setPreviewRetry((count) => count + 1);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        if (url?.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      previewUrlsRef.current = {};
    };
  }, []);

  const downloadAttachment = useCallback(
    async (attachment: MessageAttachment): Promise<string | null> => {
      const displayName = attachment.filename || "file";
      const downloadFileName = getDownloadableFileName(attachment.url);

      if (downloadFileName) {
        if (!shareId && (!userId || !chatId)) {
          throw new Error("Attachment context is missing");
        }
        const requestUrl = buildAttachmentRequestUrl({
          baseUrl: chatApiBaseUrl,
          fileName: downloadFileName,
          shareId,
          userId,
          chatId,
        });
        if (!requestUrl) {
          throw new Error("Attachment request URL is invalid");
        }

        if (isInApp) {
          const { taskId } = await startDownload({
            url: requestUrl,
            fileName: displayName,
            headers: requestHeaders,
            showSavePanel: true,
          });
          return taskId;
        }

        const response = await fetch(requestUrl, {
          headers: requestHeaders,
        });
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = displayName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        return null;
      }

      if (
        /^https?:\/\//i.test(attachment.url) ||
        attachment.url.startsWith("data:")
      ) {
        // App 内 WebView 对 window.open 往往无法触发下载；https 直链统一走原生 DownloadManager
        if (isInApp && /^https?:\/\//i.test(attachment.url)) {
          const { taskId } = await startDownload({
            url: attachment.url,
            fileName: displayName,
            mimeType: attachment.mediaType || "",
            headers: requestHeaders,
            showSavePanel: true,
          });
          return taskId;
        }
        window.open(attachment.url, "_blank", "noopener,noreferrer");
        return null;
      } else {
        throw new Error("Unsupported attachment URL");
      }
    },
    [
      chatApiBaseUrl,
      userId,
      chatId,
      shareId,
      requestHeaders,
      isInApp,
      startDownload,
    ],
  );

  const handleLightboxDownload = useCallback(
    async (attachment: MessageAttachment) => {
      if (fileDownloadCapability === "unsupported") {
        OspToast.info({
          content: I18n.t("Please update the app to access files"),
        });
        return;
      }
      setDownloading(true);
      // let waitingForTask = false;
      try {
        const taskId = await downloadAttachment(attachment);
        if (taskId) {
          // waitingForTask = true;
          setActiveTask({ taskId, mode: "lightbox" });
          OspToast.info({ content: I18n.t("Download started") });
        }
      } catch (error) {
        if (isLikelyFileDownloadNotSupportedError(error)) {
          OspToast.info({
            content: I18n.t("Please update the app to access files"),
          });
        } else {
          OspToast.error({ content: I18n.t("Failed to load attachment") });
        }
      } finally {
        setDownloading(false);
      }
    },
    [downloadAttachment, fileDownloadCapability],
  );

  const handleOpenAttachment = useCallback(
    async (attachment: MessageAttachment) => {
      if (fileDownloadCapability === "unsupported") {
        OspToast.info({
          content: I18n.t("Please update the app to access files"),
        });
        return;
      }
      setLoadingName(attachment.filename || "file");
      let waitingForTask = false;
      try {
        const taskId = await downloadAttachment(attachment);
        if (taskId) {
          waitingForTask = true;
          setActiveTask({ taskId, mode: "open" });
          OspToast.info({ content: I18n.t("Download started") });
        }
      } catch (error) {
        if (isLikelyFileDownloadNotSupportedError(error)) {
          OspToast.info({
            content: I18n.t("Please update the app to access files"),
          });
        } else {
          OspToast.error({ content: I18n.t("Failed to load attachment") });
        }
      } finally {
        if (!waitingForTask) {
          setLoadingName("");
        }
      }
    },
    [downloadAttachment, fileDownloadCapability],
  );

  useEffect(() => {
    if (!activeTask || !activeTaskState) {
      return;
    }
    if (activeTaskState.status === "completed") {
      OspToast.success({ content: I18n.t("Download completed") });
      if (activeTask.mode === "open") {
        setLoadingName("");
      } else {
        setDownloading(false);
      }
      setActiveTask(null);
      return;
    }
    if (activeTaskState.status === "failed") {
      OspToast.error({
        content:
          activeTaskState.errorMessage || I18n.t("Failed to load attachment"),
      });
      if (activeTask.mode === "open") {
        setLoadingName("");
      } else {
        setDownloading(false);
      }
      setActiveTask(null);
    }
  }, [activeTask, activeTaskState]);

  const lightboxPreviewUrl = useMemo(() => {
    if (!lightboxAttachment) {
      return "";
    }
    const key = getAttachmentKey(lightboxAttachment);
    return previewUrls[key] || getPublicPreviewUrl(lightboxAttachment.url);
  }, [lightboxAttachment, previewUrls]);

  if (!attachments.length) {
    return null;
  }

  return (
    <div
      className={cn(
        "overflow-x-auto no-scrollbar max-md:w-screen max-md:-ml-4 max-md:px-4",
        className,
      )}
    >
      <div
        className={cn("flex min-w-full w-max items-center gap-2", {
          "justify-end": align === "right",
          "justify-start": align === "left",
        })}
      >
        {attachments.map((attachment) => {
          const attachmentKey = getAttachmentKey(attachment);
          if (attachment.deleted) {
            return (
              <DeletedAttachmentChip
                key={attachmentKey}
                fileName={attachment.filename}
                subtitle={I18n.t("Removed from your vault")}
                className="h-20 md:h-24"
              />
            );
          }
          const isImage = isImageAttachment(attachment);
          const previewUrl =
            previewUrls[attachmentKey] || getPublicPreviewUrl(attachment.url);
          const previewFailed =
            isImage && !previewUrl && failedKeys.has(attachmentKey);
          const showsImage = isImage && !previewFailed;
          const isLoading =
            loadingName === attachment.filename || (showsImage && !previewUrl);
          const chipVisual = getSenderAttachmentChipVisual(attachment.filename);

          return (
            <button
              type="button"
              data-openreplay-label="chatbox_filetagattachmentlist_open"
              key={attachmentKey}
              onClick={() => {
                if (previewFailed) {
                  return retryPreview(attachmentKey);
                }
                if (!isImage) {
                  return handleOpenAttachment(attachment);
                }
                if (previewUrl) {
                  setLightboxAttachment(attachment);
                }
              }}
              data-loading={isLoading}
              className={cn(
                "button shrink-0 flex flex-col justify-between loading:flex-center rounded-3 size-20 md:size-24 overflow-hidden bg-background_solid_neutral_primary loading:bg-background_solid_neutral_tertiary break-all group",
                { "p-2": !showsImage },
              )}
            >
              <div
                className={cn(
                  "relative flex size-6 shrink-0 items-center justify-center rounded-1 group-loading:hidden",
                  chipVisual.iconBoxClassName,
                  { hidden: showsImage },
                )}
              >
                <Icon
                  name={previewFailed ? "refresh" : chipVisual.iconName}
                  size={16}
                  color={ospThemes.colors.content_inverse_primary}
                />
              </div>
              <span
                className={cn(
                  "body_s text-content_secondary line-clamp-2 group-loading:!hidden",
                  { "!hidden": showsImage },
                )}
              >
                {attachment.filename}
              </span>
              <Icon
                name="ic_loading2"
                size={24}
                className="animate-spin group-loading:block hidden"
                color={ospThemes.colors.content_accent5_secondary}
              />
              <NextImage
                src={previewUrl}
                alt={attachment.filename}
                width={96}
                height={96}
                className={cn(
                  "w-full h-full object-cover group-loading:hidden",
                  {
                    hidden: !showsImage,
                  },
                )}
              />
              <span
                className={cn("text-content_secondary group-loading:body_s", {
                  hidden: showsImage || !isLoading,
                })}
              >
                {I18n.t("Download")}
              </span>
            </button>
          );
        })}
      </div>
      {lightboxAttachment && lightboxPreviewUrl && (
        <ImageLightbox
          src={lightboxPreviewUrl}
          alt={lightboxAttachment.filename}
          onClose={() => setLightboxAttachment(null)}
          onDownload={() => handleLightboxDownload(lightboxAttachment)}
          downloading={downloading}
        />
      )}
    </div>
  );
};

export default FileTagAttachmentList;
